import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, Language, Translations } from './translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: translations.en,
  isRTL: false,
});

const VALID_LANGS = ['en', 'ar', 'az', 'fr', 'it', 'pt', 'es'] as const;

function getLangFromUrl(): Language | null {
  const param = new URLSearchParams(window.location.search).get('lang');
  return VALID_LANGS.includes(param as Language) ? (param as Language) : null;
}

function updateUrlLang(lang: Language) {
  const url = new URL(window.location.href);
  if (lang === 'en') {
    url.searchParams.delete('lang');
  } else {
    url.searchParams.set('lang', lang);
  }
  window.history.replaceState(null, '', url.toString());
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const fromUrl = getLangFromUrl();
    if (fromUrl) return fromUrl;
    const saved = localStorage.getItem('cookai-language');
    return (saved as Language) || 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('cookai-language', lang);
    updateUrlLang(lang);
  };

  // Sync URL on initial load (e.g. if lang came from localStorage, reflect it in URL)
  useEffect(() => {
    updateUrlLang(language);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRTL = language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language], isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export interface LangOption {
  code: Language;
  label: string;
  nativeName: string;
  flag: string;
}

export const LANGUAGES: LangOption[] = [
  { code: 'en', label: 'English',    nativeName: 'English',    flag: '🇬🇧' },
  { code: 'ar', label: 'Arabic',     nativeName: 'العربية',    flag: '🇸🇦' },
  { code: 'az', label: 'Azerbaijani',nativeName: 'Azərbaycan', flag: '🇦🇿' },
  { code: 'fr', label: 'French',     nativeName: 'Français',   flag: '🇫🇷' },
  { code: 'it', label: 'Italian',    nativeName: 'Italiano',   flag: '🇮🇹' },
  { code: 'pt', label: 'Portuguese', nativeName: 'Português',  flag: '🇧🇷' },
  { code: 'es', label: 'Spanish',    nativeName: 'Español',    flag: '🇪🇸' },
];
